// Tracker de actividad para Vue 2 + Firestore v9
// - Una sesión (documento) por ruta visitada (switchPage).
// - Distingue pestañas con tabId (sessionStorage).
// - Mide sólo tiempo "activo": pestaña visible + con foco, sin ociosidad > idleMs.
// - Persiste en Firestore en hidden/pagehide/beforeunload y por autosave opcional.

import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import moment from "moment";
import { getDeviceInfo } from "@/utils/deviceInfo/deviceInfo.js";

function getOrCreateTabId() {
  const k = "appTabId";
  let id = sessionStorage.getItem(k);
  if (!id) {
    id = crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    sessionStorage.setItem(k, id);
  }
  return id;
}

export default function createActivityTrackerFirestore({
  db,
  idUsuario,
  idEmpresa,
  milisegundosInactividad = 60000,
  coleccion = "activitySessions",
  ruta = location.pathname,
}) {
  if (!db) throw new Error("db (Firestore) es obligatorio");
  if (!idUsuario) throw new Error("idUsuario es obligatorio");

  // Estado de sesión (se renueva en cada switchPage)
  let sessionId = null;
  let sessionRef = null;
  const tabId = getOrCreateTabId();

  // Acumulación de tiempo activo
  let active = false;
  let lastTick = null;
  let lastUserEvent = Date.now();
  let accumMs = 0;

  // Device info (se carga una sola vez)
  let device = null;

  // Timers/handlers
  const userEvents = [
    "pointerdown",
    "pointermove",
    "keydown",
    "wheel",
    "scroll",
    "touchstart",
    "click",
  ];
  let idleTimer = null;
  let autosaveTimer = null;
  let boundVisibility = null;
  let boundPageHide = null;
  let boundBeforeUnload = null;

  const isTabInteractable = () =>
    document.visibilityState === "visible" && document.hasFocus();

  function enterActive() {
    if (!active && isTabInteractable()) {
      active = true;
      lastTick = Date.now();
    }
  }
  function exitActive() {
    if (active) {
      accumMs += Date.now() - (lastTick || Date.now());
      active = false;
      lastTick = null;
    }
  }
  function bumpActivity() {
    lastUserEvent = Date.now();
    if (isTabInteractable()) enterActive();
  }
  function idleCheck() {
    const idle = Date.now() - lastUserEvent > milisegundosInactividad;
    if (idle) exitActive();
    else if (isTabInteractable()) enterActive();
  }

  async function persist(reason = "auto") {
    if (!sessionRef) return;
    exitActive();

    const activeSeconds = Math.max(0, Math.round(accumMs / 1000));
    if (activeSeconds <= 0) return;

    const payload = {
      id: sessionId,
      idEmpresa: idEmpresa,
      idTab: tabId,
      ruta: ruta,
      tiempoActivo: activeSeconds,
      motivo: reason,
      dispositivo: device || null,
      registro: {
        idUsuario: idUsuario,
        filtro: moment().format("YYYY-MM-DD"),
        estado: "Creado",
        creacion: serverTimestamp(),
        actualizacion: serverTimestamp(),
      },
    };
    
    try {
      await setDoc(sessionRef, payload, { merge: true });
    } catch {
      //
    }
  }

  function newSession(newPageId, reason = "switch") {
    // no esperar persist para no bloquear navegación
    void persist(reason);

    // reinicia acumuladores
    active = false;
    lastTick = null;
    lastUserEvent = Date.now();
    accumMs = 0;

    // define nueva sesión
    sessionId = crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    ruta = newPageId || ruta;
    sessionRef = doc(db, coleccion, sessionId);

    if (isTabInteractable()) enterActive();
  }

  async function loadDeviceOnce() {
    if (!device) {
      try {
        device = await getDeviceInfo();
      } catch {
        device = { source: "unknown" };
      }
    }
  }

  // API
  async function start() {
    await loadDeviceOnce();

    userEvents.forEach((ev) =>
      window.addEventListener(ev, bumpActivity, { passive: true })
    );
    window.addEventListener("focus", enterActive);
    window.addEventListener("blur", exitActive);

    boundVisibility = async () => {
      if (document.visibilityState === "hidden") {
        await persist("hidden");
        exitActive();
      } else {
        enterActive();
      }
    };
    document.addEventListener("visibilitychange", boundVisibility);

    idleTimer = setInterval(idleCheck, 1000);

    boundPageHide = () => {
      void persist("pagehide");
    };
    window.addEventListener("pagehide", boundPageHide, { capture: true });

    boundBeforeUnload = () => {
      void persist("beforeunload");
    };
    window.addEventListener("beforeunload", boundBeforeUnload, {
      capture: true,
    });

    // sesión inicial para la ruta actual
    newSession(ruta, "initial");
  }

  function stop(reason = "manual-stop") {
    window.removeEventListener("focus", enterActive);
    window.removeEventListener("blur", exitActive);
    if (boundVisibility)
      document.removeEventListener("visibilitychange", boundVisibility);
    if (boundPageHide)
      window.removeEventListener("pagehide", boundPageHide, { capture: true });
    if (boundBeforeUnload)
      window.removeEventListener("beforeunload", boundBeforeUnload, {
        capture: true,
      });
    userEvents.forEach((ev) =>
      window.removeEventListener(ev, bumpActivity, { passive: true })
    );
    if (idleTimer) clearInterval(idleTimer);
    if (autosaveTimer) clearInterval(autosaveTimer);
    void persist(reason);
  }

  function autosave(everySeconds = 30) {
    if (autosaveTimer) clearInterval(autosaveTimer);
    autosaveTimer = setInterval(() => {
      void persist("autosave");
    }, everySeconds * 1000);
  }

  function switchPage(newPageId, reason = "route") {
    newSession(newPageId, reason);
  }

  return {
    start,
    stop,
    autosave,
    switchPage,
    getSessionId: () => sessionId,
    getTabId: () => tabId,
  };
}
